import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { format, fromUnixTime } from 'date-fns';

import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import {
  selectMFControlPanelRegion,
  selectMFControlPanelModels,
  selectMFControlPanelForecastUnixDate,
  selectMFControlPanelPeriod,
  selectMFControlPanelDateOffset,
  selectMFControlPanelHeatmapImages,
  selectMFControlPanelConsultRequestId,
  selectMFControlPanelLastConsulted,
} from 'redux/reducers/meteorologyFieldsControlPanelReducer';
import useImages from 'utils/hooks/meteorology/numericForecast/useImages';
import useImagesDownload from 'utils/hooks/meteorology/numericForecast/useImagesDownload';
import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import { REGIONS } from './filterOptions';
import ControlPanelSheet from './ControlPanelSheet';
import SpatialDisplay from './SpatialDisplay';
import HeatmapDisplay from './HeatmapDisplay';
import DownloadButton from './DownloadButton';

import './index.scss';

export default function NumericForecastMobile() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [gridMode, setGridMode] = useState(false);

  const forecastUnixDate = useSelector(selectMFControlPanelForecastUnixDate);
  const models = useSelector(selectMFControlPanelModels);
  const region = useSelector(selectMFControlPanelRegion);
  const period = useSelector(selectMFControlPanelPeriod);
  const dateOffset = useSelector(selectMFControlPanelDateOffset) || 0;
  const heatmapImages = useSelector(selectMFControlPanelHeatmapImages);
  const consultRequestId = useSelector(selectMFControlPanelConsultRequestId);
  const lastConsulted = useSelector(selectMFControlPanelLastConsulted);
  const isConsultedHeatmap = lastConsulted?.viewMode === 'heatmap';
  const periodLabel = !isConsultedHeatmap && !gridMode && lastConsulted?.period
    ? `${lastConsulted.period[0]}+${dateOffset}`
    : undefined;
  const dateLabel = !gridMode && forecastUnixDate
    ? format(fromUnixTime(forecastUnixDate), 'dd.MM.yy')
    : undefined;

  const { variables } = useControlPanelOptions('pnt');
  const variableLabel = variables.find((v) => v.id === lastConsulted?.variable)?.label;
  const viewModeLabel = isConsultedHeatmap ? 'Heatmap' : 'Espacial';
  const regionLabel = REGIONS.find((r) => r.value === lastConsulted?.region)?.label;
  const modelsCount = lastConsulted?.models?.length ?? 0;

  const showInstructions = consultRequestId === 0
    && !forecastUnixDate && !region && !period && models.length === 0;

  const {
    images, isFetching, error, areAllImagesFetched,
  } = useImages(gridMode);
  const { onDownloadClick, downloadDisabled } = useImagesDownload(images, areAllImagesFetched);

  useHandleRequestErrors(error as ResponseError);

  return (
    <div className="tf-pnt-mobile">
      <QueryCard>
        {lastConsulted && (
          <QueryCardHeader
            label="Consulta atual"
            title={variableLabel ? `${variableLabel} · ${viewModeLabel}` : viewModeLabel}
            subtitle={`${modelsCount} modelo(s)${regionLabel ? ` · ${regionLabel}` : ''}`}
          />
        )}
        <div className="tf-pnt-mobile-current-query-row">
          <QueryCardTrigger onClick={() => setPanelOpen(true)} padded={false} />
          {!isConsultedHeatmap && (
            <DownloadButton onClick={onDownloadClick} disabled={downloadDisabled} />
          )}
        </div>
      </QueryCard>

      {showInstructions && (
        <div className="tf-pnt-mobile-instructions">
          <p className="tf-pnt-mobile-instructions-title">Configure e realize a consulta</p>
          <p className="tf-pnt-mobile-instructions-body">
            Escolha a variável, os modelos e a data no painel de controle
            para gerar os mapas de previsão.
          </p>
        </div>
      )}

      {!showInstructions && isConsultedHeatmap && (
        <HeatmapDisplay
          images={heatmapImages}
          fetching={isFetching}
          forecastDate={
            lastConsulted?.forecastUnixDate
              ? format(fromUnixTime(lastConsulted.forecastUnixDate), 'dd/MM/yyyy')
              : undefined
          }
        />
      )}

      {!showInstructions && !isConsultedHeatmap && (
        <SpatialDisplay
          images={images}
          models={lastConsulted?.models ?? []}
          gridMode={gridMode}
          setGridMode={setGridMode}
          periodLabel={periodLabel}
          dateLabel={dateLabel}
        />
      )}

      <ControlPanelSheet open={panelOpen} dismiss={() => setPanelOpen(false)} />
    </div>
  );
}
